import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { XMarkIcon } from '@heroicons/react/24/outline';
import apiClient from '../../../api/axios';

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface CreateGroupData {
  name: string;
  description: string;
  groupType: string;
  isPrivate: boolean;
  excludeFromBillingRate: boolean;
  maxMembers?: number;
  color?: string;
  icon?: string;
}

export const CreateGroupModal: React.FC<CreateGroupModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<CreateGroupData>({
    name: '',
    description: '',
    groupType: 'custom',
    isPrivate: false,
    excludeFromBillingRate: false,
    maxMembers: undefined,
    color: '#3B82F6',
    icon: ''
  });

  const queryClient = useQueryClient();

  const createGroupMutation = useMutation({
    mutationFn: async (groupData: CreateGroupData) => {
      const response = await apiClient.post('/admin/groups', groupData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['all-groups-for-user'] }); // ← Fix for UserGroupsModal
      onSuccess();
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createGroupMutation.mutate(formData);
  };

  const groupTypeOptions = [
    { value: 'department', label: 'Department' },
    { value: 'practice_team', label: 'Practice Team' },
    { value: 'project_team', label: 'Project Team' },
    { value: 'administrative', label: 'Administrative' },
    { value: 'client_team', label: 'Client Team' },
    { value: 'custom', label: 'Custom' }
  ];

  const iconOptions = [
    { value: '', label: 'No Icon' },
    { value: '👥', label: 'Team' },
    { value: '🏢', label: 'Department' },
    { value: '📋', label: 'Project' },
    { value: '⚖️', label: 'Legal' },
    { value: '💰', label: 'Billing' },
    { value: '📞', label: 'Support' },
    { value: '🔧', label: 'Technical' }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Create New Group
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Group Name *
            </label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              required
              placeholder="Enter group name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
              placeholder="Describe the group's purpose..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Group Type *
            </label>
            <select
              value={formData.groupType}
              onChange={(e) => setFormData(prev => ({ ...prev, groupType: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {groupTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Icon
            </label>
            <select
              value={formData.icon}
              onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {iconOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label} {option.value}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Color
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-md"
              />
              <Input
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                placeholder="#3B82F6"
                className="flex-1"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Maximum Members (Optional)
            </label>
            <Input
              type="number"
              value={formData.maxMembers || ''}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                maxMembers: e.target.value ? parseInt(e.target.value) : undefined 
              }))}
              placeholder="Leave empty for unlimited"
              min="1"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isPrivate}
                onChange={(e) => setFormData(prev => ({ ...prev, isPrivate: e.target.checked }))}
                className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Private Group (only admins can see)
              </span>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={formData.excludeFromBillingRate}
                onChange={(e) => setFormData(prev => ({ ...prev, excludeFromBillingRate: e.target.checked }))}
                className="rounded border-gray-300 text-lex-accent focus:ring-lex-accent"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Exclude from billing rate calculations
              </span>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createGroupMutation.isPending}
            >
              {createGroupMutation.isPending ? 'Creating...' : 'Create Group'}
            </Button>
          </div>
        </form>

        {createGroupMutation.isError && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md">
            <p className="text-red-600 dark:text-red-400 text-sm">
              Failed to create group. Please try again.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}; 